import React from 'react';
import { AlertTriangle } from 'lucide-react';

export interface LLMUsageProgressBarProps {
  /** € included in the plan (the 100% mark) */
  limit: number;
  /** € consumed in the current cycle */
  used: number;
  /** € over the limit (0 when under) */
  overage?: number;
  /** Warning threshold marker (default 80) */
  warningThresholdPct?: number;
  /** Currency symbol (default €) */
  currencySymbol?: string;
}

/**
 * LLM usage progress bar for **fixed** packages.
 * - Shows usage progress from 0% → 100% with marks at 80% and 100%.
 * - When the merchant surpasses 100%, the bar caps visually but we show
 *   the extra usage in € next to it.
 */
const LLMUsageProgressBar: React.FC<LLMUsageProgressBarProps> = ({
  limit,
  used,
  overage = 0,
  warningThresholdPct = 80,
  currencySymbol = '€',
}) => {
  const safeLimit = limit > 0 ? limit : 0;
  const rawPct = safeLimit > 0 ? (used / safeLimit) * 100 : 0;
  const isOver = rawPct >= 100 || overage > 0;
  const isWarning = rawPct >= warningThresholdPct && !isOver;

  // Visual fill is capped at 100%; overage is communicated via the label.
  const fillPct = Math.min(rawPct, 100);

  const barColor = isOver
    ? 'bg-red-500'
    : isWarning
      ? 'bg-yellow-500'
      : 'bg-primary';

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">
            LLM usage
          </span>
          <span className="text-base font-semibold dark:text-text-primary text-light-text-primary">
            {currencySymbol}{used.toFixed(2)}
            <span className="text-xs font-normal dark:text-text-secondary text-light-text-secondary">
              {' '}/ {currencySymbol}{safeLimit.toFixed(2)}
            </span>
          </span>
        </div>
        <span
          className={`text-sm font-semibold ${
            isOver
              ? 'text-red-500'
              : isWarning
                ? 'text-yellow-500'
                : 'text-primary'
          }`}
        >
          {rawPct.toFixed(0)}%
        </span>
      </div>

      <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500 rounded-full`}
          style={{ width: `${fillPct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(rawPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Threshold markers */}
      <div className="relative w-full h-3 -mt-3 pointer-events-none">
        <div
          className="absolute top-0 h-3 w-px bg-gray-400 dark:bg-gray-500"
          style={{ left: `${warningThresholdPct}%` }}
          aria-hidden
        />
        <div
          className="absolute top-0 h-3 w-px bg-gray-400 dark:bg-gray-500"
          style={{ left: '100%', transform: 'translateX(-1px)' }}
          aria-hidden
        />
      </div>

      <div className="relative w-full mt-1 text-[10px] dark:text-text-secondary text-light-text-secondary">
        <span
          className="absolute"
          style={{ left: `${warningThresholdPct}%`, transform: 'translateX(-50%)' }}
        >
          {warningThresholdPct}%
        </span>
        <span className="absolute right-0">100%</span>
        <span className="invisible">.</span>
      </div>

      {isOver && (
        <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-red-500/10 text-red-600 dark:text-red-300 border border-red-500/30">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Over plan limit by{' '}
            <span className="font-semibold">
              {currencySymbol}{(overage || used - safeLimit).toFixed(2)}
            </span>{' '}
            this cycle
          </span>
        </div>
      )}
    </div>
  );
};

export default LLMUsageProgressBar;
